import { MaintenanceItem } from '../types/shift';
import { ShiftSummaryRecord } from '../services/shiftApi';
import { extractDateFromText } from './extractDate';

export interface DerivedMaintenance {
  customer: string;
  ticketNumber: string;
  description: string;
  status: string;
  scheduledDate: string;
  scheduledEndDate?: string | null;
  tppTicketNumber?: string;
  submittedBy: string;
  shiftDate: string;
  shiftType: string;
  shiftId: string;
}

function parseItems(json: string | null | undefined): MaintenanceItem[] {
  if (!json) return [];
  try {
    let p = JSON.parse(json);
    if (typeof p === 'string') p = JSON.parse(p);
    return Array.isArray(p) ? (p as MaintenanceItem[]) : [];
  } catch {
    return [];
  }
}

/**
 * Flatten maintenance items from every shift into a deduped list keyed by
 * (ticketNumber, scheduledDate). Resolves scheduled dates from status text
 * when the scheduledDate field is empty — mirrors Calendar behavior.
 */
export function deriveMaintenance(summaries: ShiftSummaryRecord[]): DerivedMaintenance[] {
  const flat: DerivedMaintenance[] = [];
  for (const s of summaries) {
    for (const m of parseItems(s.maintenanceItemsJson)) {
      const date = m.scheduledDate || extractDateFromText(m.status, m.description) || '';
      if (!date) continue;
      flat.push({
        customer: m.customer,
        ticketNumber: m.ticketNumber,
        description: m.description,
        status: m.status,
        scheduledDate: date,
        scheduledEndDate: m.scheduledEndDate,
        tppTicketNumber: m.tppTicketNumber,
        submittedBy: (s as ShiftSummaryRecord & { submittedBy?: string }).submittedBy || s.onDutyTech,
        shiftDate: s.date,
        shiftType: s.shift,
        shiftId: s.id,
      });
    }
  }
  const seen = new Set<string>();
  return flat.filter((e) => {
    const key = `${e.ticketNumber}-${e.scheduledDate}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function upcomingWithinDays(items: DerivedMaintenance[], days: number): DerivedMaintenance[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setDate(end.getDate() + days);
  return items
    .filter((m) => {
      const d = new Date(`${m.scheduledDate}T00:00`);
      return !Number.isNaN(d.getTime()) && d >= today && d <= end;
    })
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}
